import { app as teamsApp, authentication } from '@microsoft/teams-js';
import { authLogger } from '../utils/logger';

/**
 * Erkennt, ob die App innerhalb eines Microsoft-Teams-Tabs läuft, und stellt
 * eine schmale Wrapper-API um das Teams-JS-SDK bereit.
 *
 * `app.initialize()` läuft auch außerhalb von Teams durch (z.B. im normalen
 * Browser), aber `app.getContext()` schlägt dort fehl. Das nutzen wir als
 * Detektor.
 */

let detectionPromise: Promise<boolean> | null = null;
let isInTeamsCached: boolean | null = null;

export function detectTeamsContext(): Promise<boolean> {
  if (isInTeamsCached !== null) {
    return Promise.resolve(isInTeamsCached);
  }
  if (detectionPromise) {
    return detectionPromise;
  }

  detectionPromise = (async () => {
    try {
      // Time-out, damit der Browser-Pfad nicht ewig hängt, falls initialize()
      // im Iframe-fremden Kontext nie auflöst.
      await Promise.race([
        teamsApp.initialize(),
        new Promise<never>((_, rej) => setTimeout(() => rej(new Error('teams-init-timeout')), 1500)),
      ]);
      await teamsApp.getContext();
      isInTeamsCached = true;
      authLogger.info('Teams-Kontext erkannt');
      return true;
    } catch (err) {
      isInTeamsCached = false;
      authLogger.info('Kein Teams-Kontext (Browser-Modus)', { reason: (err as Error).message });
      return false;
    }
  })();

  return detectionPromise;
}

/**
 * Holt einen SSO-Token vom Teams-Client (synchron-blockierend für den User —
 * Teams cached den Token intern). Die `resources`-Option ist mittlerweile
 * optional; Teams liefert einen Token mit `aud = api://{clientId}` der
 * registrierten Anwendung.
 */
export async function getTeamsSsoToken(): Promise<string> {
  return authentication.getAuthToken();
}
